/**
 * Nova implementação do ParentsAuthContext com realtime simples
 * Seguindo documentação oficial do Supabase
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { dataHelpers, supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useRealtimeStudents } from '../hooks/useRealtimeStudents';

const ParentsAuthContext = createContext({});

export const useParentsAuth = () => {
  const context = useContext(ParentsAuthContext);
  if (!context) {
    throw new Error('useParentsAuth must be used within a ParentsAuthProvider');
  }
  return context;
};

export const ParentsAuthProvider = ({ children }) => {
  const [parent, setParent] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [lastBalanceUpdate, setLastBalanceUpdate] = useState(0);

  // Função para processar atualizações de estudantes via realtime
  const handleStudentUpdate = (updatedStudent) => {
    console.log('📝 Processando atualização de estudante via realtime:', {
      id: updatedStudent.id,
      balance: updatedStudent.balance,
      timestamp: Date.now()
    });

    // Atualizar lista de estudantes
    setStudents(prevStudents => {
      return prevStudents.map(student => 
        student.id === updatedStudent.id 
          ? { ...student, ...updatedStudent }
          : student
      );
    });

    // Atualizar estudante selecionado se necessário
    setSelectedStudent(prevSelected => {
      if (prevSelected && prevSelected.id === updatedStudent.id) {
        return { ...prevSelected, ...updatedStudent };
      }
      return prevSelected;
    });

    // Atualizar localStorage
    try {
      const savedAuth = JSON.parse(localStorage.getItem('parents_auth') || '{}');
      if (savedAuth.students) {
        savedAuth.students = savedAuth.students.map(student => 
          student.id === updatedStudent.id 
            ? { ...student, ...updatedStudent }
            : student
        );
        
        if (savedAuth.selectedStudent && savedAuth.selectedStudent.id === updatedStudent.id) {
          savedAuth.selectedStudent = { ...savedAuth.selectedStudent, ...updatedStudent };
        }
        
        localStorage.setItem('parents_auth', JSON.stringify(savedAuth));
      }
    } catch (error) {
      console.warn('Erro ao atualizar localStorage:', error);
    }

    // Forçar re-render
    setLastBalanceUpdate(Date.now());
    
    console.log('✅ Estudante atualizado com sucesso');
  };

  // Hook para realtime dos estudantes
  const studentIds = students && students.length > 0 ? students.map(s => s.id) : [];
  const { isConnected, status, reconnect } = useRealtimeStudents(
    studentIds.length > 0 ? studentIds : null,
    handleStudentUpdate
  );

  // Carregar dados salvos no localStorage e configurar listener de auth
  useEffect(() => {
    const loadSavedAuth = () => {
      try {
        const savedAuth = localStorage.getItem('parents_auth');
        if (savedAuth) {
          const authData = JSON.parse(savedAuth);
          if (authData.parent && authData.students) {
            setParent(authData.parent);
            setStudents(authData.students);
            setSelectedStudent(authData.selectedStudent || authData.students[0] || null);
            setAuthenticated(true);
            console.log('📱 Dados carregados do localStorage:', {
              parent: authData.parent.name,
              studentsCount: authData.students.length
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error);
        localStorage.removeItem('parents_auth');
      }
    };

    loadSavedAuth();
  }, []);

  // Função de login
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      console.log('🔐 Iniciando login...');

      // Buscar pai por email na tabela people
      const { data: parents, error: parentError } = await supabase
        .from('people')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .eq('type', 'parent')
        .single();

      if (parentError || !parents) {
        throw new Error('Email não encontrado ou não é um responsável');
      }

      // Verificar senha simples (se existir campo password na tabela people)
      // Caso não tenha campo password, pular esta verificação
      // if (parents.password && parents.password !== password) {
      //   throw new Error('Senha incorreta');
      // }

      // Buscar estudantes do pai (usando person_id)
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .eq('person_id', parents.id)
        .order('name');

      if (studentsError) {
        throw new Error('Erro ao carregar estudantes');
      }

      // Salvar dados
      const userAuthData = {
        parent: parents,
        students: studentsData || [],
        selectedStudent: studentsData?.[0] || null,
        loginTime: new Date().toISOString()
      };

      localStorage.setItem('parents_auth', JSON.stringify(userAuthData));
      
      setParent(parents);
      setStudents(studentsData || []);
      setSelectedStudent(studentsData?.[0] || null);
      setAuthenticated(true);

      console.log('✅ Login realizado com sucesso');
      toast.success('Login realizado com sucesso!');

    } catch (error) {
      console.error('❌ Erro no login:', error);
      toast.error(error.message || 'Erro no login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Função de logout
  const signOut = () => {
    localStorage.removeItem('parents_auth');
    setParent(null);
    setStudents([]);
    setSelectedStudent(null);
    setAuthenticated(false);
    setLastBalanceUpdate(0);
    
    console.log('👋 Logout realizado');
    toast.success('Logout realizado com sucesso!');
  };

  // Função para atualizar dados
  const refreshData = async () => {
    if (!parent) return;

    try {
      setLoading(true);
      
      const { data: studentsData, error } = await supabase
        .from('students')
        .select('*')
        .eq('person_id', parent.id)
        .order('name');

      if (error) {
        throw error;
      }

      setStudents(studentsData || []);
      
      // Atualizar localStorage
      const updatedAuthData = {
        parent,
        students: studentsData || [],
        selectedStudent: selectedStudent,
        loginTime: new Date().toISOString()
      };
      localStorage.setItem('parents_auth', JSON.stringify(updatedAuthData));

      console.log('🔄 Dados atualizados');

    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setLoading(false);
    }
  };

  // Função para trocar estudante selecionado
  const switchStudent = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setSelectedStudent(student);
      
      // Atualizar localStorage
      try {
        const savedAuth = JSON.parse(localStorage.getItem('parents_auth') || '{}');
        savedAuth.selectedStudent = student;
        localStorage.setItem('parents_auth', JSON.stringify(savedAuth));
        console.log('👤 Estudante selecionado:', student.name);
      } catch (error) {
        console.warn('Erro ao atualizar estudante selecionado:', error);
      }
    }
  };

  // Função para testar conectividade do realtime
  const testRealtimeConnectivity = () => {
    console.log('🧪 Testando conectividade do realtime...');
    console.log('📊 Estado atual:', {
      isConnected,
      status,
      studentsCount: students.length,
      studentIds: students.map(s => s.id)
    });
    
    if (isConnected) {
      console.log('✅ Realtime está conectado');
      toast.success('Realtime conectado!');
    } else {
      console.log('❌ Realtime não está conectado');
      toast.error('Realtime desconectado');
    }
  };

  const value = {
    // Auth state
    parent,
    students,
    selectedStudent,
    loading,
    authenticated,
    lastBalanceUpdate,

    // Realtime state
    realtimeConnected: isConnected,
    realtimeStatus: status,

    // Functions
    signIn,
    signOut,
    refreshData,
    setSelectedStudent,
    switchStudent,
    testRealtimeConnectivity,
    forceRealtimeReconnect: reconnect,

    // Helper properties
    isAuthenticated: authenticated && !!parent && students.length > 0
  };

  return (
    <ParentsAuthContext.Provider value={value}>
      {children}
    </ParentsAuthContext.Provider>
  );
};